/**
 * Smart JSON fetcher that auto-detects gzip-compressed responses.
 *
 * The large geo overlays (airspace, runway-zones-large) are no longer gzipped:
 * they are coordinate-rounded and split into <22 MB uncompressed chunk files by
 * scripts/chunk-geojson.mjs, which Cloudflare serves plainly. This helper still
 * detects the gzip magic bytes (0x1f 0x8b) and decompresses transparently as a
 * safety net for any other file that might be pre-gzipped, and otherwise parses
 * plain JSON directly.
 */
export async function fetchJSON<T = any>(url: string): Promise<T> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Failed to fetch ${url}: ${res.status}`);

  const buffer = await res.arrayBuffer();
  const bytes = new Uint8Array(buffer);

  // Gzip magic bytes: 0x1f 0x8b
  if (bytes.length >= 2 && bytes[0] === 0x1f && bytes[1] === 0x8b) {
    const ds = new DecompressionStream('gzip');
    const decompressed = new Blob([buffer]).stream().pipeThrough(ds);
    return new Response(decompressed).json();
  }

  // Plain JSON — decode directly
  return JSON.parse(new TextDecoder().decode(buffer));
}
